<script setup lang="ts">
import { ref } from 'vue'

import { getClasses } from '~/js/composables/_fiftyfiveplus/classes'

import { Analytics } from '~/js/interfaces/_fiftyfiveplus/analytics'


interface Props {
    badge?: number | null,
    content?: string,
    label?: string,
    disabled?: boolean
    ga?: Analytics | null,
    icon?: string,
    iconPosition?: 'before' | 'after',
    type?: 'a' | 'button' | 'div',
    url?: string | null,
    utilities?: string
    utilitiesIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
    badge: null,
    content: '',
    label: '',
    disabled: false,
    iconPosition: 'before',
    size: 'default',
    type: 'button',
    url: null,
    utilities: '',
    utilitiesIcon: '',
})

const classes = ref({
    _default: 'inline-flex items-center justify-center group',
    animation: 'transition-colors duration-300',
    border: '',
    color: 'bg-dark-burgundy text-white',
    font: 'font-primary text-sm md:text-base',
    radius: 'rounded-full',
    spacing: '',
    size: 'h-10 w-10',
})

const classesDisabled = ref({
    border: 'border-gray-400',
    color: 'text-gray-400',
})

const classesEnabled = ref({
    border: 'border-blue-900',
    color: 'text-blue-900',
    focus: 'focus:ring-2 focus:ring-offset-2 focus:ring-yellow focus:outline-none',
    hover: 'hover:text-white hover:bg-claret',
})

</script>

<template>
    <component
        :is="disabled ? 'div' : type"
        :class="[
            getClasses(classes), 
            disabled ? getClasses(classesDisabled) : getClasses(classesEnabled),
            iconPosition == 'after' ? 'flex-row-reverse' : 'flex-row',
            utilities
        ]"
        :aria-label="label"
        :tabindex="disabled ? -1 : 0"
        :href="url"
        :data-tag-category="type != 'div' ? ga?.category ?? '' : false"
        :data-tag-action="type != 'div' ? ga?.action ?? '' : false"
        :data-tag-label="type != 'div' ? ga?.label ?? '' : false"
    >
        <span class="order-2 whitespace-nowrap flex items-center">
            <span>{{ content }}</span>
            <span
                v-if="badge"
                class="bg-euromex-red-700 ml-2 text-white font-normal text-xs rounded-full block w-5 h-5 flex items-center justify-center grow-0 shrink-0"
            >
                {{ badge }}
            </span>
        </span>

        <span 
            v-if="icon"
            :class="[
                content.length > 0 ? (iconPosition == 'after' ? 'ml-2' : 'mr-2') : '',
                utilitiesIcon
            ]"
        >
            <i :class="icon"/>
        </span>
    </component>
</template>